/**
 * DomainMailTestTab — 收件域右侧"写信发送"Tab
 * 以该收件域下的邮箱身份发送测试邮件（发件邮箱下拉过滤到选中域），并提供该域的调试收件箱。
 */
import { useMemo, useRef, useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Spinner,
  TabList,
  Tab,
  Field,
  Textarea,
  Combobox,
  Option,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components'
import { SendRegular, MailInboxRegular, EyeRegular, AttachRegular, DismissRegular } from '@fluentui/react-icons'
import { composeApi, type ComposeSendResult } from '@/api/compose'
import { testApi } from '@/api/test'
import { usersApi } from '@/api/users'
import type { Domain } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import SimpleList, { type SimpleListItem } from '@/components/SimpleList'

type SubTab = 'send' | 'inbox'

interface LocalFile {
  name: string
  data: string // base64
  type: string
}

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 },
  form: { display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '760px' },
  rawBox: {
    fontFamily: 'monospace',
    fontSize: '13px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    backgroundColor: tokens.colorNeutralBackground2,
    padding: '12px',
    borderRadius: tokens.borderRadiusMedium,
    maxHeight: '420px',
    overflow: 'auto',
  },
  attRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' },
  attName: { flex: 1, fontSize: '13px', color: tokens.colorNeutralForeground2, wordBreak: 'break-all' },
  resultBox: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px',
    fontSize: '13px',
    lineHeight: '1.6',
  },
})

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      resolve((result.split(',')[1] ?? ''))
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

export default function DomainMailTestTab({ domain }: { domain: Domain }) {
  const styles = useStyles()
  const queryClient = useQueryClient()

  // 该收件域下的可用发件邮箱（用于下拉）
  const { data: users } = useQuery({
    queryKey: ['users-compose'],
    queryFn: () => usersApi.list(1, 500),
  })
  const mailboxOptions = useMemo(
    () => (users ?? []).filter((u) => u.domain === domain.name).map((u) => u.email).filter(Boolean),
    [users, domain.name],
  )

  const [subTab, setSubTab] = useState<SubTab>('send')
  const [from, setFrom] = useState('')
  const [recipients, setRecipients] = useState('')
  const [subject, setSubject] = useState('')
  const [bodyText, setBodyText] = useState('')
  const [bodyHtml, setBodyHtml] = useState('')
  const [attachments, setAttachments] = useState<LocalFile[]>([])
  const [result, setResult] = useState<ComposeSendResult | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const [inboxAddr, setInboxAddr] = useState('')
  const [viewRaw, setViewRaw] = useState<string | null>(null)

  // 切换域名时：发件邮箱与收件箱地址默认取该域第一个邮箱
  useEffect(() => {
    setSubTab('send')
    setResult(null)
    setViewRaw(null)
    setAttachments([])
    if (mailboxOptions.length > 0) {
      setFrom(mailboxOptions[0])
      setInboxAddr(mailboxOptions[0])
    }
  }, [domain.id])

  // 域名已加载出邮箱但状态为空时补默认值
  useEffect(() => {
    if (mailboxOptions.length > 0 && !from) {
      setFrom(mailboxOptions[0])
      setInboxAddr(mailboxOptions[0])
    }
  }, [mailboxOptions, from])

  const sendMutation = useMutation({
    mutationFn: () =>
      composeApi.send({
        from,
        recipients: recipients.split(/[,，;；\s]+/).map((s) => s.trim()).filter(Boolean),
        subject,
        body_html: bodyHtml,
        body_text: bodyText,
        attachments: attachments.map((a) => ({ filename: a.name, content_type: a.type, data: a.data })),
      }),
    onSuccess: (r) => {
      setResult(r)
      toast.success('发送完成', `成功 ${r.sent} / 失败 ${r.failed}`)
      if (r.failed === 0) {
        setSubject('')
        setBodyHtml('')
        setBodyText('')
        setAttachments([])
      }
    },
    onError: (err) => {
      setResult(null)
      toast.error('发送失败', err instanceof ApiError ? err.message : '发送失败')
    },
  })

  const { data: inbox, isLoading: inboxLoading } = useQuery({
    queryKey: ['test-inbox', inboxAddr, subTab],
    queryFn: () => testApi.inbox(inboxAddr),
    enabled: subTab === 'inbox',
  })

  const viewMessage = (id: number) => {
    testApi.message(id)
      .then((d) => setViewRaw(d.content))
      .catch((e) => toast.error('读取失败', e instanceof ApiError ? e.message : '读取失败'))
  }

  const inboxItems: SimpleListItem[] = (inbox ?? []).map((m) => ({
    key: String(m.id),
    title: m.subject || '(无主题)',
    description: [m.from, m.size != null ? `${m.size} B` : '', m.created_at].filter(Boolean).join(' · '),
    extra: <Button appearance="subtle" size="small" icon={<EyeRegular />} onClick={() => viewMessage(m.id)}>查看</Button>,
  }))

  const onPickFiles = async (files: FileList | null) => {
    if (!files) return
    const next: LocalFile[] = []
    for (const f of Array.from(files)) {
      next.push({ name: f.name, type: f.type || 'application/octet-stream', data: await readFileAsBase64(f) })
    }
    setAttachments((prev) => [...prev, ...next])
  }

  const canSend = !!from.trim() && recipients.trim().split(/[,，;；\s]+/).filter(Boolean).length > 0 && !sendMutation.isPending

  return (
    <div className={styles.root}>
      <MessageBar intent="info">
        <MessageBarBody>
          <MessageBarTitle>按收件域</MessageBarTitle>
          发件身份限定为 <b>{domain.name}</b> 下的邮箱账号。
        </MessageBarBody>
      </MessageBar>

      <TabList selectedValue={subTab} onTabSelect={(_, d) => setSubTab(d.value as SubTab)}>
        <Tab value="send" icon={<SendRegular />}>写信发送</Tab>
        <Tab value="inbox" icon={<MailInboxRegular />}>收件箱</Tab>
      </TabList>

      {subTab === 'send' && (
        <div className={styles.form}>
          <Field label="发件邮箱" required hint="仅该收件域下的本地邮箱，将以此邮箱身份发送">
            {mailboxOptions.length > 0 ? (
              <Combobox
                value={from}
                freeform
                onOptionSelect={(_, d) => setFrom(d.optionValue ?? '')}
                onChange={(e) => setFrom(e.target.value)}
                placeholder="选择或输入邮箱"
              >
                {mailboxOptions.map((em) => <Option key={em} value={em}>{em}</Option>)}
              </Combobox>
            ) : (
              <Input value={from} onChange={(_, d) => setFrom(d.value)} placeholder="user@domain" />
            )}
          </Field>

          <Field label="收件人" required hint="多个地址用逗号/分号/空格分隔，支持本地域（投递到邮箱）与外部域（入队外发）">
            <Input value={recipients} onChange={(_, d) => setRecipients(d.value)} placeholder="a@example.com, b@example.com" />
          </Field>

          <Field label="主题" required>
            <Input value={subject} onChange={(_, d) => setSubject(d.value)} placeholder="邮件主题" />
          </Field>

          <Field label="纯文本正文">
            <Textarea rows={5} value={bodyText} onChange={(_, d) => setBodyText(d.value)} placeholder="纯文本正文（可选，作为无 HTML 客户端备选）" />
          </Field>

          <Field label="HTML 正文">
            <Textarea rows={6} value={bodyHtml} onChange={(_, d) => setBodyHtml(d.value)} placeholder="HTML 正文（可选）..." />
          </Field>

          <input
            ref={fileRef}
            type="file"
            multiple
            hidden
            onChange={(e) => { onPickFiles(e.target.files); e.target.value = '' }}
          />
          {attachments.length > 0 && (
            <div>
              {attachments.map((a, i) => (
                <div key={a.name + i} className={styles.attRow}>
                  <AttachRegular />
                  <span className={styles.attName}>{a.name}</span>
                  <Button appearance="subtle" size="small" icon={<DismissRegular />} onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}>移除</Button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Button appearance="primary" icon={<SendRegular />} disabled={!canSend} onClick={() => sendMutation.mutate()}>
              发送邮件
            </Button>
            <Button icon={<AttachRegular />} onClick={() => fileRef.current?.click()}>添加附件</Button>
          </div>

          {result && (
            <div className={styles.resultBox}>
              <Text weight="semibold">发送结果</Text>
              <div>主题：{result.subject}</div>
              <div>成功 {result.sent} / 失败 {result.failed} / 共 {result.total}</div>
              {result.results?.map((r) => (
                <div key={r.recipient} style={{ color: r.status === 'failed' ? tokens.colorStatusDangerForeground1 : tokens.colorStatusSuccessForeground1 }}>
                  {r.recipient} — {r.status}{r.error ? `：${r.error}` : ''}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {subTab === 'inbox' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 12, flexWrap: 'wrap' }}>
            <Input
              value={inboxAddr}
              onChange={(_, d) => setInboxAddr(d.value)}
              placeholder="user@domain"
              style={{ width: 280 }}
              onKeyDown={(e) => { if (e.key === 'Enter') queryClient.invalidateQueries({ queryKey: ['test-inbox', inboxAddr, 'inbox'] }) }}
            />
            <Button icon={inboxLoading ? <Spinner size="tiny" /> : <MailInboxRegular />} onClick={() => queryClient.invalidateQueries({ queryKey: ['test-inbox', inboxAddr, 'inbox'] })}>刷新</Button>
          </div>
          <SimpleList items={inboxItems} loading={inboxLoading} emptyText="暂无邮件，点击刷新加载" />
        </div>
      )}

      <Dialog open={viewRaw !== null} onOpenChange={(_, d) => { if (!d.open) setViewRaw(null) }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>邮件内容</DialogTitle>
            <DialogContent>
              <div className={styles.rawBox}>{viewRaw}</div>
            </DialogContent>
            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '8px' }}>
              <Button appearance="primary" onClick={() => setViewRaw(null)}>关闭</Button>
            </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}